import axios from "axios";
import type { EventCeremony, EventFamily } from "./eventSettings";
import type { EventTemplateDefinition } from "@/lib/event-templates";
import { API_ROUTES } from "@/routes";
import { PREVIEW_PARAM } from "@/utils/previewLink";

const API_BASE_URL = import.meta.env.VITE_API_URL || "https://jubiel.local/api";

// LocalStorage key prefix for event access tokens (stored by slug for convenience)
const EVENT_ACCESS_TOKEN_PREFIX = "jubiel_event_access_";

/**
 * Get stored access token for a specific event (by slug)
 */
export function getEventAccessToken(eventSlug: string): string | null {
  try {
    const stored = localStorage.getItem(`${EVENT_ACCESS_TOKEN_PREFIX}${eventSlug}`);
    if (!stored) return null;

    const { token, expiresAt } = JSON.parse(stored);

    // Check if token has expired
    if (expiresAt && Date.now() / 1000 > expiresAt) {
      localStorage.removeItem(`${EVENT_ACCESS_TOKEN_PREFIX}${eventSlug}`);
      return null;
    }

    return token;
  } catch {
    return null;
  }
}

/**
 * Store access token for a specific event (by slug)
 */
export function storeEventAccessToken(
  eventSlug: string,
  token: string,
  expiresIn: number
): void {
  try {
    const expiresAt = Math.floor(Date.now() / 1000) + expiresIn;
    localStorage.setItem(
      `${EVENT_ACCESS_TOKEN_PREFIX}${eventSlug}`,
      JSON.stringify({ token, expiresAt })
    );
  } catch (e) {
    console.warn("Failed to store event access token:", e);
  }
}

/**
 * Clear access token for a specific event (by slug)
 */
export function clearEventAccessToken(eventSlug: string): void {
  try {
    localStorage.removeItem(`${EVENT_ACCESS_TOKEN_PREFIX}${eventSlug}`);
  } catch {
    // Ignore errors
  }
}

/**
 * Gate tokens that opened an event page in this tab, keyed by every address
 * of that event (slug, uuid) — the page may be opened by one and its
 * sections may call the API by the other.
 */
const sessionGateTokens = new Map<string, string>();

function rememberGateToken(token: string, ...identifiers: Array<string | null | undefined>): void {
  for (const id of identifiers) {
    if (id) sessionGateTokens.set(id.replace(/^@/, ""), token);
  }
}

/**
 * JUB-151 follow-up: every public sub-resource of an event (gift list,
 * contributions, payment intent, charge confirm/status, RSVP) applies the same
 * gate as the page, so those calls must carry what opened the page — the
 * gate token of a password-protected event (Authorization: Bearer) and, for
 * the owner's preview, the preview token (query). Reads only: the API ignores
 * the preview token on write endpoints.
 */
export function publicEventRequestConfig(identifier: string): {
  headers: Record<string, string>;
  params?: Record<string, string>;
} {
  const id = identifier.replace(/^@/, "");
  const headers: Record<string, string> = { Accept: "application/json" };
  const gateToken = sessionGateTokens.get(id) ?? getEventAccessToken(id);
  if (gateToken) {
    headers["Authorization"] = `Bearer ${gateToken}`;
  }
  const previewToken = typeof window !== "undefined"
    ? new URLSearchParams(window.location.search).get(PREVIEW_PARAM)
    : null;

  return previewToken ? { headers, params: { [PREVIEW_PARAM]: previewToken } } : { headers };
}

export interface PublicEvent {
  uuid: string;
  name: string;
  /** JUB-154: null while the couple has not decided the date. */
  date: string | null;
  type: number;
  description?: string;
  starBride?: string;
  starGroom?: string;
  starBaptism?: string;
  starBaptism2?: string;
  includesWedding?: boolean;
  includesBaptism?: boolean;
  babyCount?: number | null;
  hashtag?: string | null;
  cover?: string | null;
  currency: string;
  country: string;
  /** Present in the public payload and used by every template; was missing from this type. */
  city?: string | null;
  venue?: string | null;
  url: string;
  live?: boolean;
  active?: boolean;
  giftsActive?: boolean;
  createdDate?: string;
  theme?: string;
  /** Always sent by EventResource (defaults to 'ro'); absent only on the redirect_to payload. */
  defaultLanguage?: string;
  fontFamily?: string | null;
  fontScale?: "small" | "medium" | "large" | null;
  story?: string | null;
  gallery?: string[];
  passwordEnabled?: boolean;
  searchable?: boolean;
  registryPublic?: boolean;
  showTimeline?: boolean;
  showGallery?: boolean;
  showStory?: boolean;
  colorScheme?: string | null;
  heroText?: { ro?: string; en?: string } | null;
  rsvpCutoffDate?: string | null;
  family?: EventFamily;
  ceremonies?: {
    civil?: EventCeremony;
    religious?: EventCeremony;
    party?: EventCeremony;
    /** JUB-154: a baptism held separately from the religious wedding. */
    baptism?: EventCeremony;
  };
  template?: EventTemplateDefinition;
  rsvpSettings?: {
    rsvpEnabled: boolean;
    dueDate?: string | null;
    kidsEnabled: boolean;
    accommodationEnabled: boolean;
    mealOptions: {
      standard: boolean;
      vegan: boolean;
      vegetarian: boolean;
    };
  };
  /**
   * JUB-73/JUB-64: set when the requested slug is the event's OLD slug.
   * The backend no longer answers with an HTTP 302 (the XHR followed it into
   * the SPA route and rendered a blank page) — it returns this field instead
   * and the page loader must navigate to the new slug client-side.
   * When present, no other event fields are populated.
   */
  redirect_to?: string;
  /**
   * JUB-151: true only when the request carried a valid owner preview token.
   * Draft overrides from the URL are applied only then.
   */
  previewMode?: boolean;
  /**
   * JUB-225: guest/household context resolved from ?g=<guest_uuid> via /e/{code}.
   */
  invitedGuest?: {
    uuid: string;
    familyName?: string | null;
    name?: string | null;
    email?: string | null;
    phone?: string | null;
  } | null;
}

/**
 * Get a public event by slug (no authentication required)
 * @param slug - Event URL slug
 * @param accessToken - Optional access token for password-protected events
 */
export async function getPublicEvent(
  slug: string,
  accessToken?: string | null,
  previewToken?: string | null
): Promise<PublicEvent> {
  const headers: Record<string, string> = {
    Accept: "application/json",
  };

  // Add Authorization header if access token is provided
  if (accessToken) {
    headers["Authorization"] = `Bearer ${accessToken}`;
  }

  const guestUuid =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("g")
      : null;
  const params: Record<string, string> = {};
  if (previewToken) {
    params.preview = previewToken;
  }
  if (guestUuid) {
    params.g = guestUuid;
  }

  // The owner's server-signed preview token (JUB-151) is what lifts the live
  // gate and the password gate for the owner's own preview (JUB-125). The API
  // also answers with an HttpOnly cookie that lets <img> load the event's
  // protected images — no credential is ever put in an image URL.
  const response = await axios.get<PublicEvent>(API_ROUTES.events.public(slug), {
    headers,
    params: Object.keys(params).length > 0 ? params : undefined,
  });

  const data = response.data;
  if (accessToken) {
    rememberGateToken(accessToken, slug, data.uuid, data.url);
  }

  // Parse gallery if it's a JSON string
  if (data.gallery && typeof data.gallery === "string") {
    try {
      data.gallery = JSON.parse(data.gallery);
    } catch (e) {
      console.warn("Failed to parse gallery JSON:", e);
      data.gallery = [];
    }
  }

  return data;
}

export type PublicGiftItem = {
  id: string;
  name: string;
  description?: string | null;
  amount: number;
  location?: string | null;
  link?: string | null;
  cover?: string | null;
  order: number;
  enabled: boolean;
  cash: boolean;
  category?: {
    id: string | null; // UUID
    name: string;
  } | null;
  /** Not included in public API responses; present only when fetched by the event owner */
  totalContributions?: number;
  /** Not included in public API responses; present only when fetched by the event owner */
  remainingAmount?: number;
  currency: string;
};

export type PublicGiftIndexResponse = {
  gifts: PublicGiftItem[];
  categories: Array<{
    id: string | null;
    name: string;
  }>;
  /**
   * JUB-53: true only when the event owner has a connected Stripe account with
   * charges enabled. When false the registry is still shown, but the card pay
   * flow must be replaced with the informational note (envelope path stays).
   */
  cardPaymentsEnabled?: boolean;
  /**
   * JUB-53: connected account id for Stripe.js initialisation. Set ONLY in
   * direct-charge (owner fee-bearer) mode — null for destination-charge modes,
   * where the PaymentIntent lives on the platform account.
   */
  stripeAccountId?: string | null;
};

/**
 * JUB-98: guest self-serve contribution status lookup (backs the "Verifică o
 * contribuție" affordance). Mirrors the JUB-79 backend response shape — no
 * guest-identifying fields (name/email) are returned by design.
 */
export type PublicContributionStatus = {
  status: "pending" | "paid" | "refunded";
  amount: number;
  currency: string;
  giftName: string | null;
  reference: string;
  createdAt: string | null;
};

/**
 * Look up a contribution's status by its reference code (charge_public_uuid).
 * Public, unauthenticated, rate-limited server-side. Callers must treat any
 * error (404, 429, etc.) as "cannot show a result" — see the controller for
 * why a wrong code never reveals whether it belongs to someone else.
 */
export async function getPublicContributionStatus(
  slug: string,
  chargeUuid: string
): Promise<PublicContributionStatus> {
  const response = await axios.get<PublicContributionStatus>(
    `${API_BASE_URL}/events/public/${slug}/contributions/${chargeUuid}`,
    publicEventRequestConfig(slug)
  );
  return response.data;
}

/**
 * Get public gifts for an event by slug (no authentication required)
 * @param slug - Event URL slug
 * @param accessToken - Optional access token for password-protected events
 * @param previewToken - Optional owner preview token (JUB-151)
 */
export async function getPublicEventGifts(
  slug: string,
  accessToken?: string | null,
  previewToken?: string | null
): Promise<PublicGiftIndexResponse> {
  const config = publicEventRequestConfig(slug);
  if (accessToken) {
    config.headers["Authorization"] = `Bearer ${accessToken}`;
  }

  // The registry follows the page's own gate (PublicEventGate, allowPreview), so
  // the owner's preview of an unpublished page reads it with the same signed
  // preview token the page itself was opened with (JUB-151, JUB-161).
  const response = await axios.get<PublicGiftIndexResponse>(
    `${API_BASE_URL}/events/public/${slug}/gifts`,
    previewToken ? { ...config, params: { preview: previewToken } } : config
  );

  return response.data;
}

/**
 * Validate password for password-protected event
 * On success, stores the access token in localStorage for subsequent requests
 */
export async function validateEventPassword(
  slug: string,
  password: string
): Promise<{
  success: boolean;
  message?: string;
  remainingAttempts?: number;
  retryAfter?: number;
  accessToken?: string;
}> {
  try {
    const response = await axios.post<{
      success: boolean;
      message?: string;
      access_token?: string;
      expires_in?: number;
    }>(
      `${API_BASE_URL}/events/public/${slug}/validate-password`,
      { password },
      {
        headers: {
          Accept: "application/json",
        },
      }
    );

    // Store the access token in localStorage if provided (using slug as key)
    if (response.data.success && response.data.access_token) {
      storeEventAccessToken(
        slug,
        response.data.access_token,
        response.data.expires_in || 86400 // Default to 24 hours
      );
    }

    return {
      ...response.data,
      accessToken: response.data.access_token,
    };
  } catch (error: unknown) {
    const axiosError = error as { response?: { status?: number; data?: { error?: { message?: string; remainingAttempts?: number; retryAfter?: number } } } };
    if (axiosError.response?.status === 429) {
      return {
        success: false,
        message: axiosError.response?.data?.error?.message || "Too many failed attempts",
        retryAfter: axiosError.response?.data?.error?.retryAfter,
      };
    }
    if (axiosError.response?.status === 401) {
      return {
        success: false,
        message: axiosError.response?.data?.error?.message || "Invalid password",
        remainingAttempts: axiosError.response?.data?.error?.remainingAttempts,
        // The final failed attempt returns 401 (not 429) WITH retryAfter — without
        // forwarding it the UI never locks when attempts span page reloads.
        retryAfter: axiosError.response?.data?.error?.retryAfter,
      };
    }
    throw error;
  }
}

